import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { calculateValueScore } from '../lib/calculations';
import { getValueBadgeClass } from '../lib/constants';
import AppLayout from '../components/layout/AppLayout';
import type { ProductDetails } from '../types/strain';
import SearchableSelect from '../components/forms/SearchableSelect';
import {
    Calculator,
    Zap,
    Activity,
    Calendar,
    Wind,
    Brain,
    CheckCircle2,
    ChevronRight,
    Package,
    Plus,
    Clock,
    Sparkles,
    Scale
} from 'lucide-react';

export default function NewSession() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Available data
    const [products, setProducts] = useState<ProductDetails[]>([]);

    // Form state
    const [productId, setProductId] = useState('');
    const [totalPackagePrice, setTotalPackagePrice] = useState(35);
    const [totalPackageWeight, setTotalPackageWeight] = useState(3.5);
    const [dosageAmount, setDosageAmount] = useState(0.5);
    const [dosageUnit, setDosageUnit] = useState('g');
    const [timeOfDay, setTimeOfDay] = useState(() => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Morning';
        if (hour < 17) return 'Afternoon';
        if (hour < 21) return 'Evening';
        return 'Night';
    });
    const [consumptionMethod, setConsumptionMethod] = useState('Flower');
    const [dateUsed, setDateUsed] = useState(new Date().toISOString().split('T')[0]);
    const [onsetMinutes, setOnsetMinutes] = useState(15);
    const [peakMinutes, setPeakMinutes] = useState(30);
    const [durationMinutes, setDurationMinutes] = useState(120);
    const [overallRating, setOverallRating] = useState(7);
    const [anxiety, setAnxiety] = useState(5);
    const [bodyHigh, setBodyHigh] = useState(5);
    const [headHigh, setHeadHigh] = useState(5);
    const [creativity, setCreativity] = useState(5);
    const [smoothness, setSmoothness] = useState(5);
    const [notes, setNotes] = useState('');

    // UI state
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Calculated fields
    const sessionCost = (totalPackagePrice / totalPackageWeight) * dosageAmount;
    const valueScore = calculateValueScore(overallRating, durationMinutes, sessionCost);

    // Fetch data on mount
    useEffect(() => {
        fetchProducts();
    }, []);

    // Handle "Join Session" pre-fill
    useEffect(() => {
        if (location.state && location.state.cloneSession) {
            const clone = location.state.cloneSession;

            if (clone.product_id) setProductId(clone.product_id);
            if (clone.consumption_method) setConsumptionMethod(clone.consumption_method);
        }
    }, [location]);

    const fetchProducts = async () => {
        const { data, error } = await supabase
            .from('products')
            .select(`
        *,
        strain:strains(*),
        brand:brands(*)
      `)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Error fetching products:', error);
        } else if (data) {
            setProducts(data as ProductDetails[]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;

        setLoading(true);
        setError('');

        const { error: insertError } = await supabase.from('sessions').insert({
            product_id: productId || null,
            user_id: user.id,
            total_package_price: totalPackagePrice,
            total_package_weight: totalPackageWeight,
            dosage_amount: dosageAmount,
            dosage_unit: dosageUnit,
            session_cost: sessionCost,
            time_of_day: timeOfDay,
            consumption_method: consumptionMethod,
            date_used: dateUsed,
            onset_minutes: onsetMinutes,
            peak_minutes: peakMinutes,
            duration_minutes: durationMinutes,
            overall_rating: overallRating,
            anxiety,
            body_high: bodyHigh,
            head_high: headHigh,
            creativity,
            smoothness,
            notes: notes || null,
        });

        if (insertError) {
            setError(insertError.message);
            setLoading(false);
        } else {
            setSuccess(true);
            setTimeout(() => {
                navigate('/');
            }, 2000);
        }
    };

    if (success) {
        return (
            <AppLayout title="Log Saved">
                <div className="flex flex-col items-center justify-center min-h-[70vh] animate-in fade-in zoom-in-95 duration-500 px-6">
                    <div className="relative mb-12">
                        <div className="absolute inset-0 bg-emerald-500/20 blur-3xl rounded-full scale-150 animate-pulse-slow" />
                        <div className="relative bg-dark-surface p-10 rounded-[40px] border border-emerald-500/30 shadow-2xl shadow-emerald-500/10">
                            <CheckCircle2 size={72} className="text-emerald-500" />
                        </div>
                    </div>

                    <h2 className="text-3xl font-black mb-4 tracking-tight">Session Recorded</h2>
                    <p className="text-dark-muted mb-10 text-center max-w-xs leading-relaxed">
                        Your insights have been added to the dashboard. Redirecting shortly...
                    </p>

                    <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
                        <div className="card bg-dark-bg/50 border-dark-border/50 text-center p-6">
                            <p className="text-[10px] font-bold text-dark-muted uppercase tracking-widest mb-1">Cost</p>
                            <p className="text-xl font-black">${sessionCost.toFixed(2)}</p>
                        </div>
                        <div className="card bg-dark-bg/50 border-dark-border/50 text-center p-6">
                            <p className="text-[10px] font-bold text-dark-muted uppercase tracking-widest mb-1">Value Score</p>
                            <p className={`text-xl font-black ${getValueBadgeClass(valueScore)}`}>{valueScore}</p>
                        </div>
                    </div>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout>
            <div className="max-w-xl mx-auto mb-24 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-black tracking-tight italic">
                            Track<span className="text-accent-purple ml-1">Session</span>
                        </h2>
                        <p className="text-[10px] font-black uppercase tracking-widest text-dark-muted mt-1 opacity-70">Real-time value analysis</p>
                    </div>
                    <div className="bg-dark-surface/40 p-3 rounded-2xl border border-white/5 backdrop-blur-md shadow-inner">
                        <Activity size={20} className="text-accent-purple animate-pulse-slow" />
                    </div>
                </div>

                {/* Live Value Score Display */}
                <div className="card bg-gradient-to-br from-accent-purple/10 via-dark-surface/20 to-dark-bg/40 border-accent-purple/20 relative overflow-hidden group shadow-2xl">
                    <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-700">
                        <Sparkles size={120} />
                    </div>
                    <div className="grid grid-cols-2 gap-6 relative">
                        <div className="text-center border-r border-dark-border/40 pr-6 py-4">
                            <p className="text-[10px] font-black text-dark-muted uppercase tracking-widest mb-1 opacity-60">Session Cost</p>
                            <p className="text-4xl font-black tracking-tighter text-white">${sessionCost.toFixed(2)}</p>
                            <div className="flex items-center justify-center gap-1.5 mt-2 transition-transform">
                                <Scale size={10} className="text-accent-purple/60" />
                                <p className="text-[9px] font-bold text-dark-muted">
                                    {dosageAmount}{dosageUnit} @ ${totalPackagePrice}
                                </p>
                            </div>
                        </div>
                        <div className="text-center pl-6 py-4">
                            <p className="text-[10px] font-black text-dark-muted uppercase tracking-widest mb-1 opacity-60">Value Index</p>
                            <p className={`text-4xl font-black tracking-tighter transition-all ${getValueBadgeClass(valueScore)}`}>
                                {valueScore}
                            </p>
                            <div className="flex items-center justify-center gap-1.5 mt-2">
                                <Zap size={10} className="text-amber-500/60" />
                                <p className="text-[9px] font-bold text-dark-muted uppercase tracking-tighter">Efficiency Score</p>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="bg-accent-red/10 border border-accent-red/20 text-accent-red px-4 py-3 rounded-2xl text-[11px] font-bold animate-in slide-in-from-top-2">
                            {error}
                        </div>
                    )}

                    {/* Product Selection */}
                    <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-5 relative z-30">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-accent-purple/10 rounded-lg">
                                    <Package size={14} className="text-accent-purple" />
                                </div>
                                <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">Inventory</h3>
                            </div>
                            <div className="flex gap-4">
                                <Link to="/products" className="text-[9px] font-black uppercase tracking-widest text-accent-purple hover:text-white transition-colors flex items-center gap-1">
                                    <Plus size={10} strokeWidth={3} /> Product
                                </Link>
                                <Link to="/strains" className="text-[9px] font-black uppercase tracking-widest text-accent-purple hover:text-white transition-colors flex items-center gap-1">
                                    <Plus size={10} strokeWidth={3} /> Strain
                                </Link>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-dark-muted uppercase tracking-widest ml-1">What are you consuming?</label>
                            <SearchableSelect
                                placeholder="Select product from your kit..."
                                options={products.map((product) => ({
                                    id: product.id,
                                    label: `${product.strain?.name || 'Unknown'} - ${product.brand?.name || 'Unknown'}`,
                                    subLabel: `${product.product_type}${product.thc_percent ? ` • ${product.thc_percent}% THC` : ''}`
                                }))}
                                value={productId}
                                onChange={setProductId}
                            />
                        </div>

                        {products.length === 0 && (
                            <div className="p-4 bg-dark-bg/60 rounded-2xl border border-dark-border border-dashed">
                                <p className="text-[10px] font-black text-amber-500/80 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                    <Zap size={12} strokeWidth={3} /> Setup required
                                </p>
                                <p className="text-xs text-dark-muted leading-relaxed font-medium">
                                    You haven't added any products yet. Go to <Link to="/products" className="text-accent-purple hover:underline font-bold">Products</Link> to link a strain to a brand first.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Dose Calculator */}
                    <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-accent-purple/10 rounded-lg">
                                    <Calculator size={14} className="text-accent-purple" />
                                </div>
                                <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">Dose Logic</h3>
                            </div>
                            <span className="text-[9px] bg-dark-bg border border-white/5 text-dark-muted px-2 py-1 rounded-lg font-black uppercase tracking-tighter shadow-inner">
                                Scale Sync: {dosageUnit}
                            </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2 group">
                                <label className="text-[10px] font-black text-dark-muted uppercase tracking-widest ml-1 group-focus-within:text-accent-purple transition-colors">Investment ($)</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-muted font-bold">$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={totalPackagePrice}
                                        onChange={(e) => setTotalPackagePrice(Number(e.target.value))}
                                        className="input h-14 pl-8 text-sm font-black bg-dark-bg/50 border-white/5 focus:border-accent-purple/50"
                                        placeholder="35.00"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2 group">
                                <label className="text-[10px] font-black text-dark-muted uppercase tracking-widest ml-1 group-focus-within:text-accent-purple transition-colors">Pack Net Wt.</label>
                                <div className="flex gap-2">
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={totalPackageWeight}
                                        onChange={(e) => setTotalPackageWeight(Number(e.target.value))}
                                        className="input h-14 text-sm font-black bg-dark-bg/50 border-white/5 focus:border-accent-purple/50 flex-1"
                                        placeholder="3.5"
                                    />
                                    <select
                                        value={dosageUnit}
                                        onChange={(e) => setDosageUnit(e.target.value)}
                                        className="bg-dark-bg/80 border border-white/5 rounded-2xl text-[11px] font-black uppercase tracking-tighter px-3 h-14 focus:outline-none focus:border-accent-purple/50 shadow-inner"
                                    >
                                        <option value="g">G</option>
                                        <option value="mg">MG</option>
                                        <option value="ea">UNIT</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-5 bg-dark-bg/30 p-5 rounded-[24px] border border-white/5 shadow-inner">
                            <div className="flex justify-between items-end">
                                <label className="text-[10px] font-black text-dark-muted uppercase tracking-[0.1em]">
                                    Visual Scale: <span className="text-white ml-2 text-xs">{dosageAmount} {dosageUnit}</span>
                                </label>
                                <p className="text-[9px] font-black text-accent-purple/80 bg-accent-purple/5 px-2 py-1 rounded-lg border border-accent-purple/10 uppercase tracking-widest">
                                    Cost per dose: ${(sessionCost || 0).toFixed(2)}
                                </p>
                            </div>
                            <input
                                type="range"
                                min="0.05"
                                max={totalPackageWeight}
                                step="0.05"
                                value={dosageAmount}
                                onChange={(e) => setDosageAmount(Number(e.target.value))}
                                className="slider w-full"
                            />
                        </div>
                    </div>

                    {/* Context & Method */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-4">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-accent-purple/10 rounded-lg">
                                    <Calendar size={14} className="text-accent-purple/60" />
                                </div>
                                <h3 className="text-[10px] font-black text-dark-muted uppercase tracking-[0.2em]">Timeline</h3>
                            </div>
                            <div className="space-y-3">
                                <div className="relative group">
                                    <select
                                        value={timeOfDay}
                                        onChange={(e) => setTimeOfDay(e.target.value)}
                                        className="input text-xs font-black cursor-pointer h-12 bg-dark-bg/50 border-white/5 focus:border-accent-purple/50 appearance-none pl-4"
                                    >
                                        <option value="Morning">Morning</option>
                                        <option value="Afternoon">Afternoon</option>
                                        <option value="Evening">Evening</option>
                                        <option value="Night">Night</option>
                                    </select>
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-dark-muted">
                                        {timeOfDay === 'Morning' ? '🌅' : timeOfDay === 'Afternoon' ? '☀️' : timeOfDay === 'Evening' ? '🌇' : '🌙'}
                                    </div>
                                </div>
                                <input
                                    type="date"
                                    value={dateUsed}
                                    onChange={(e) => setDateUsed(e.target.value)}
                                    className="input text-[11px] font-black px-4 h-12 bg-dark-bg/50 border-white/5 focus:border-accent-purple/50"
                                />
                            </div>
                        </div>

                        <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-4">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-accent-purple/10 rounded-lg">
                                    <Wind size={14} className="text-accent-purple/60" />
                                </div>
                                <h3 className="text-[10px] font-black text-dark-muted uppercase tracking-[0.2em]">Method</h3>
                            </div>
                            <div className="grid grid-cols-1 gap-1.5 h-[96px] overflow-y-auto scrollbar-hide">
                                {['Bong', 'Pipe', 'Joint', 'Vape', 'Edible', 'Dab'].map((method) => (
                                    <button
                                        key={method}
                                        type="button"
                                        onClick={() => setConsumptionMethod(method)}
                                        className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest text-left transition-all ${consumptionMethod === method
                                            ? 'bg-accent-purple text-white shadow-lg shadow-accent-purple/20 border-accent-purple/50'
                                            : 'bg-dark-bg/40 border border-white/5 text-dark-muted hover:text-white'
                                            }`}
                                    >
                                        {method}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Timing */}
                    <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-8">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-accent-purple/10 rounded-lg">
                                <Clock size={16} className="text-accent-purple" />
                            </div>
                            <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">Pharmacology</h3>
                        </div>

                        {[
                            { label: 'Onset', value: onsetMinutes, setter: setOnsetMinutes, max: 60, icon: '⚡' },
                            { label: 'Peak', value: peakMinutes, setter: setPeakMinutes, max: 120, icon: '🏔️' },
                            { label: 'Total Duration', value: durationMinutes, setter: setDurationMinutes, max: 360, step: 30, icon: '⏳' },
                        ].map((time) => (
                            <div key={time.label} className="space-y-4">
                                <div className="flex justify-between items-end">
                                    <p className="text-[10px] font-black text-dark-muted uppercase tracking-widest">{time.label}</p>
                                    <p className="text-[11px] font-black text-white">{time.icon} {time.value} min</p>
                                </div>
                                <div className="bg-dark-bg/30 p-2 rounded-xl border border-white/5">
                                    <input
                                        type="range"
                                        min="0"
                                        max={time.max}
                                        step={time.step || 5}
                                        value={time.value}
                                        onChange={(e) => time.setter(Number(e.target.value))}
                                        className="slider w-full"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Effects Profile */}
                    <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-8">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-accent-purple/10 rounded-lg">
                                <Brain size={16} className="text-accent-purple" />
                            </div>
                            <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">Effect Profile</h3>
                        </div>

                        <div className="grid grid-cols-1 gap-6">
                            {[
                                { label: 'Anxiety Relief', value: anxiety, setter: setAnxiety },
                                { label: 'Body Immersion', value: bodyHigh, setter: setBodyHigh },
                                { label: 'Cerebral Depth', value: headHigh, setter: setHeadHigh },
                                { label: 'Creativity Spark', value: creativity, setter: setCreativity },
                                { label: 'Draw Smoothness', value: smoothness, setter: setSmoothness },
                            ].map((effect) => (
                                <div key={effect.label} className="space-y-3">
                                    <div className="flex justify-between items-center pr-2">
                                        <span className="text-[10px] font-black text-dark-muted uppercase tracking-widest">{effect.label}</span>
                                        <span className="text-sm font-black text-accent-purple tabular-nums">{effect.value}</span>
                                    </div>
                                    <div className="relative h-2 bg-dark-bg/80 rounded-full border border-white/5 overflow-hidden group">
                                        <div
                                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-accent-purple/40 to-accent-purple rounded-full transition-all duration-500 shadow-[0_0_12px_rgba(168,85,247,0.4)]"
                                            style={{ width: `${effect.value * 10}%` }}
                                        />
                                        <input
                                            type="range"
                                            min="0"
                                            max="10"
                                            step="0.5"
                                            value={effect.value}
                                            onChange={(e) => effect.setter(Number(e.target.value))}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Observation Log */}
                    <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-4">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-dark-muted/10 rounded-lg">
                                <Wind size={16} className="text-dark-muted" />
                            </div>
                            <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-dark-muted">Observation Log</h3>
                        </div>
                        <div className="relative">
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="input min-h-[140px] resize-none text-[13px] font-bold bg-dark-bg/50 border-white/5 p-5 leading-relaxed focus:border-accent-purple/30 transition-all placeholder:text-dark-muted/40"
                                placeholder="Flavor profile, mood shifts, or specific activities..."
                            />
                            <div className="absolute bottom-4 right-4 text-[9px] font-black text-dark-muted/30 uppercase tracking-widest">
                                Private Note
                            </div>
                        </div>
                    </div>

                    {/* Core Sentiment (Overall Rating) */}
                    <div className="card bg-dark-surface/40 backdrop-blur-md border-dark-border/40 space-y-6">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-accent-purple/10 rounded-lg">
                                    <Sparkles size={16} className="text-accent-purple" />
                                </div>
                                <h3 className="text-[11px] font-black uppercase tracking-[0.15em] text-white">Core Sentiment</h3>
                            </div>
                            <div className="flex items-end gap-1">
                                <p className="text-4xl font-black text-accent-purple tracking-tighter leading-none">{overallRating}</p>
                                <span className="text-[10px] font-black text-dark-muted uppercase mb-1 tracking-tighter">/10</span>
                            </div>
                        </div>
                        <div className="bg-dark-bg/30 p-6 rounded-[24px] border border-white/5 shadow-inner">
                            <input
                                type="range"
                                min="1"
                                max="10"
                                value={overallRating}
                                onChange={(e) => setOverallRating(Number(e.target.value))}
                                className="slider w-full"
                            />
                        </div>
                    </div>

                    {/* Feedback Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full h-16 text-base font-black uppercase tracking-[0.2em] shadow-2xl shadow-accent-purple/30 group relative overflow-hidden active:scale-95 transition-all rounded-[24px]"
                    >
                        <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="relative flex items-center justify-center gap-4">
                            {loading ? (
                                <>
                                    <Activity className="animate-spin" size={20} />
                                    Synchronizing...
                                </>
                            ) : (
                                <>
                                    Finalize Log <ChevronRight size={20} className="group-hover:translate-x-1.5 transition-transform" strokeWidth={3} />
                                </>
                            )}
                        </span>
                    </button>
                    <p className="text-center text-[9px] font-black text-dark-muted uppercase tracking-[0.25em] opacity-40">
                        Securely stored in your personal vault
                    </p>
                </form>
            </div>
        </AppLayout>
    );
}
